const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 250;
const SEARCH_ENDPOINT = '/search/suggest';
const RESOURCES =
  'resources[type]=query,product,collection&resources[limit]=6&resources[limit_scope]=each';
const SECTION_ID = 'predictive-search';

class HeaderSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');

    this.debounceTimeout = null;
    this.abortController = null;

    // Open triggers live outside this element (the header icon button)
    this._onDocClick = (e) => {
      if (e.target.closest('[data-search-open]')) {
        e.preventDefault();
        this.open();
      }
    };
    document.addEventListener('click', this._onDocClick);

    // Close triggers inside this element (close button + backdrop)
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-search-close]')) this.close();
    });

    // Escape closes
    this._onKeydown = (e) => {
      if (e.key === 'Escape' && this.hasAttribute('open')) this.close();
    };
    document.addEventListener('keydown', this._onKeydown);

    // Debounced predictive search
    this.input.addEventListener('input', () => this.onInput());
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKeydown);
  }

  open() {
    this.setAttribute('open', '');
    this.input.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => this.input.focus());
  }

  close() {
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.value = '';
    this.clearResults();
    if (this.abortController) this.abortController.abort();
    clearTimeout(this.debounceTimeout);
  }

  onInput() {
    clearTimeout(this.debounceTimeout);
    const term = this.input.value.trim();
    if (term.length < MIN_QUERY_LENGTH) {
      this.clearResults();
      return;
    }
    this.debounceTimeout = setTimeout(() => this.fetchResults(term), DEBOUNCE_MS);
  }

  fetchResults(term) {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    const url = `${SEARCH_ENDPOINT}?q=${encodeURIComponent(term)}&${RESOURCES}&section_id=${SECTION_ID}`;

    fetch(url, { signal: this.abortController.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`Search request failed: ${res.status}`);
        return res.text();
      })
      .then((text) => {
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        const html = section ? section.innerHTML.trim() : '';
        this.results.innerHTML = html;
        this.toggleAttribute('has-results', html.length > 0);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        console.error(err);
      });
  }

  clearResults() {
    this.results.innerHTML = '';
    this.removeAttribute('has-results');
  }
}

customElements.define('header-search', HeaderSearch);
